import React, { useState, useMemo, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import axios from "axios";
import { toast } from "sonner";
import { redirectToCheckout } from "../lib/stripeRedirect";
import {
  ArrowRight, ArrowLeft, Check, Loader2, Mail, Lock, User, Phone, X,
  Heart, Sparkles, GraduationCap, Briefcase, PartyPopper, RefreshCw, Calendar,
  Users, Target, Zap, Send, MessageCircle, Crown, Globe, ShieldCheck
} from "lucide-react";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

// Helper para extrair mensagem de erro segura (FastAPI pode retornar detail como array de objetos)
function extractErrorMessage(err, fallback = "Algo deu errado, tente novamente.") {
  const detail = err?.response?.data?.detail;
  if (typeof detail === "string") return detail;
  if (Array.isArray(detail) && detail.length > 0) {
    return detail.map((d) => d?.msg || d?.message || "").filter(Boolean).join(" · ") || fallback;
  }
  if (detail && typeof detail === "object") {
    return detail.msg || detail.message || fallback;
  }
  return err?.message || fallback;
}

const LiventyMark = ({ size = 36 }) => (
  <span
    style={{
      display: "inline-flex",
      alignItems: "center",
      justifyContent: "center",
      width: size,
      height: size,
      borderRadius: Math.round(size * 0.235),
      background: "linear-gradient(160deg, #FFFFFF 0%, #FAF7FF 100%)",
      boxShadow: "inset 0 1px 0 rgba(255,255,255,0.9), 0 10px 24px -10px rgba(107,61,246,0.35)",
      border: "1px solid rgba(107,61,246,0.08)",
      overflow: "hidden",
    }}
  >
    <img
      src="/brand/liventy-l-mark.webp"
      alt=""
      style={{ width: Math.round(size * 0.65), height: Math.round(size * 0.65), objectFit: "contain" }}
    />
  </span>
);

// ═════════════ STEP COMPONENTS ═════════════

function StepShell({ children, headerExtras }) {
  return (
    <div className="min-h-screen relative" style={{ backgroundColor: "#F8F7FF", fontFamily: "'Inter', system-ui, sans-serif" }}>
      <div className="absolute inset-0 pointer-events-none overflow-hidden">
        <div className="absolute" style={{ top: "20%", left: "-10%", width: "40vw", height: "40vw", borderRadius: "50%", background: "radial-gradient(50% 50% at 50% 50%, rgba(107,61,246,0.10), transparent 70%)", filter: "blur(70px)" }} />
        <div className="absolute" style={{ top: "45%", right: "-10%", width: "32vw", height: "32vw", borderRadius: "50%", background: "radial-gradient(50% 50% at 50% 50%, rgba(255,77,141,0.10), transparent 70%)", filter: "blur(70px)" }} />
      </div>
      <header className="relative z-10 max-w-3xl mx-auto px-6 py-5 flex items-center justify-between">
        <div className="flex items-center gap-2.5">
          <LiventyMark size={36} />
          <img src="/brand/liventy-wordmark-dark.webp" alt="liventy" style={{ height: 24, width: "auto" }} />
        </div>
        {headerExtras}
      </header>
      <main className="relative z-10 max-w-xl mx-auto px-6 pb-16">
        {children}
      </main>
    </div>
  );
}

function ProgressBar({ value }) {
  return (
    <div className="w-full h-1 bg-zinc-100 rounded-full overflow-hidden">
      <div
        className="h-full rounded-full transition-all duration-500 ease-out"
        style={{ width: `${value}%`, backgroundImage: "linear-gradient(95deg, #6B3DF6 0%, #FF4D8D 55%, #FFB347 100%)" }}
      />
    </div>
  );
}

// ═════════════ ONBOARDING QUESTIONS (3 etapas enxutas) ═════════════
const ONBOARDING_STEPS = [
  {
    id: "event_type",
    title: "Que tipo de evento você quer organizar?",
    subtitle: "Vamos personalizar tudo para o seu cenário.",
    type: "single",
    options: [
      { id: "wedding",     label: "Casamento",         icon: Heart },
      { id: "birthday",    label: "Aniversário",       icon: PartyPopper },
      { id: "graduation",  label: "Formatura",         icon: GraduationCap },
      { id: "corporate",   label: "Evento corporativo", icon: Briefcase },
      { id: "kids",        label: "Festa infantil",    icon: Sparkles },
      { id: "shows",       label: "Shows e eventos",   icon: Crown },
      { id: "recurring",   label: "Eventos recorrentes", icon: RefreshCw },
      { id: "other",       label: "Outros",            icon: Calendar },
    ],
  },
  {
    id: "referral_source",
    title: "Como nos conheceu?",
    subtitle: "Pra gente entender onde te alcançamos melhor.",
    type: "single",
    options: [
      { id: "instagram", label: "Instagram",  icon: Send },
      { id: "tiktok",    label: "TikTok",     icon: Sparkles },
      { id: "google",    label: "Google",     icon: Globe },
      { id: "referral",  label: "Indicação",  icon: Heart },
      { id: "youtube",   label: "YouTube",    icon: MessageCircle },
      { id: "facebook",  label: "Facebook",   icon: Users },
      { id: "other",     label: "Outros",     icon: ArrowRight },
    ],
  },
  {
    id: "usage_intent",
    title: "Como você pretende usar a Liventy?",
    subtitle: "Vamos liberar os recursos certos pro seu uso.",
    type: "single",
    options: [
      { id: "personal",     label: "Uso pessoal",       sub: "Organizar meus próprios eventos", icon: User },
      { id: "professional", label: "Uso profissional",  sub: "Organizar eventos para clientes", icon: Briefcase },
    ],
  },
];

// Sub-pergunta extra quando seleciona "profissional" no step 3
const PROFESSIONAL_TYPE_OPTIONS = [
  { id: "agencia",        label: "Agência",                    icon: Briefcase },
  { id: "cerimonialista", label: "Cerimonialista",             icon: Heart },
  { id: "organizador",    label: "Organizador de eventos",     icon: Calendar },
  { id: "produtora",      label: "Produtora",                  icon: Crown },
  { id: "outros",         label: "Outros",                     icon: Sparkles },
];

// ═════════════ PASSWORD STRENGTH ═════════════
function passwordStrength(pwd) {
  if (!pwd) return { score: 0, label: "", color: "" };
  let s = 0;
  if (pwd.length >= 6) s++;
  if (pwd.length >= 10) s++;
  if (/[A-Z]/.test(pwd) && /[a-z]/.test(pwd)) s++;
  if (/[0-9]/.test(pwd)) s++;
  if (/[^A-Za-z0-9]/.test(pwd)) s++;
  const labels = ["", "Muito fraca", "Fraca", "Média", "Forte", "Excelente"];
  const colors = ["", "bg-rose-400", "bg-orange-400", "bg-yellow-400", "bg-emerald-400", "bg-emerald-600"];
  return { score: s, label: labels[s], color: colors[s] };
}

// ═════════════ MAIN PAGE ═════════════
export default function GetStarted() {
  const navigate = useNavigate();
  const [step, setStep] = useState(0); // 0=signup, 1..5=onboarding, 6=plans, 7=redirecting
  const [signup, setSignup] = useState({ name: "", email: "", phone: "", password: "", confirm: "", accepted: false });
  const [showPwd, setShowPwd] = useState(false);
  const [answers, setAnswers] = useState({});
  const [planSelection, setPlanSelection] = useState({ tier: "personal", cycle: "annual" });
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);

  const totalSteps = 1 + ONBOARDING_STEPS.length + 1; // signup + 5 onboarding + plans
  const progress = Math.min(100, Math.round((step / totalSteps) * 100));

  const pwdStrength = useMemo(() => passwordStrength(signup.password), [signup.password]);

  // ─── Signup submit ───
  const submitSignup = async () => {
    setError(null);
    if (!signup.name.trim()) return setError("Informe seu nome completo");
    if (!signup.email.trim() || !/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(signup.email)) return setError("Email inválido");
    if (!signup.phone.trim() || signup.phone.replace(/\D/g, "").length < 10) return setError("Telefone/WhatsApp inválido");
    if (signup.password.length < 6) return setError("Senha precisa de no mínimo 6 caracteres");
    if (signup.password !== signup.confirm) return setError("As senhas não coincidem");
    if (!signup.accepted) return setError("Você precisa aceitar os Termos de Uso e a Política de Privacidade para continuar");

    setLoading(true);
    try {
      const profile_type = answers.usage_intent === "professional" ? "professional" : "personal";
      const res = await axios.post(`${API}/auth/quick-signup`, {
        name: signup.name.trim(),
        email: signup.email.trim().toLowerCase(),
        password: signup.password,
        phone: signup.phone.trim(),
        event_type: null,
        profile_type,
        accepted_terms: signup.accepted,
      });
      const token = res.data?.access_token;
      if (token) {
        localStorage.setItem("token", token);
        axios.defaults.headers.common["Authorization"] = `Bearer ${token}`;
      }
      setLoading(false);
      setStep(1);
    } catch (err) {
      setLoading(false);
      const msg = extractErrorMessage(err, "Não foi possível criar a conta");
      setError(msg);
      toast.error(msg);
    }
  };

  // ─── Onboarding answer ───
  const selectAnswer = (questionId, optionId) => {
    const next = { ...answers, [questionId]: optionId };
    setAnswers(next);

    // Caso especial: ao escolher "professional" no step 3, abrir sub-pergunta
    if (questionId === "usage_intent" && optionId === "professional" && !next.professional_type) {
      setTimeout(() => setStep(ONBOARDING_STEPS.length + 0.5), 280); // sub-step
      return;
    }

    // Auto-advance sempre (vai para próximo step ou para a página de planos)
    setTimeout(() => {
      setStep((s) => {
        // s < ONBOARDING_STEPS.length → próxima pergunta
        // s === ONBOARDING_STEPS.length → planos (step+1)
        if (s <= ONBOARDING_STEPS.length) return s + 1;
        return s;
      });
    }, 280);
  };

  // Sub-step: tipo de profissional → ao escolher, vai pra planos
  const selectProfessionalType = (typeId) => {
    setAnswers((a) => ({ ...a, professional_type: typeId }));
    setTimeout(() => setStep(ONBOARDING_STEPS.length + 1), 280);
  };

  // ─── Final save onboarding + go to plans ───
  useEffect(() => {
    if (step === ONBOARDING_STEPS.length + 1) {
      const tier = answers.usage_intent === "professional" ? "professional" : "personal";
      setPlanSelection((s) => ({ ...s, tier }));
      axios.patch(`${API}/auth/me/onboarding`, { onboarding_answers: answers }).catch(() => {});
    }
  }, [step, answers]);

  // ─── Plan checkout ───
  const startCheckout = async () => {
    setLoading(true);
    setError(null);
    try {
      // Garantir que o token está no header axios (caso outra aba tenha sobrescrito)
      const token = localStorage.getItem("token");
      if (token) {
        axios.defaults.headers.common["Authorization"] = `Bearer ${token}`;
      } else {
        // Sem token: usuário deve voltar para o cadastro
        setLoading(false);
        setError("Sessão expirou. Recomece o cadastro.");
        setStep(0);
        return;
      }

      const cycleSuffix = planSelection.cycle === "annual" ? "yearly" : "monthly";
      const tierSuffix = planSelection.tier === "professional" ? "professional" : "personal";
      const planKey = `pro_${cycleSuffix}_${tierSuffix}`;
      const res = await axios.post(`${API}/payments/checkout/session`, {
        plan: planKey,
        origin_url: window.location.origin,
      }, {
        headers: { Authorization: `Bearer ${token}` }
      });
      if (res.data?.url) {
        redirectToCheckout(res.data.url);
        return;
      }
      throw new Error("URL de pagamento não recebida");
    } catch (err) {
      setLoading(false);
      const status = err?.response?.status;
      let msg = extractErrorMessage(err, "Não foi possível iniciar o checkout");
      if (status === 401 || status === 403) {
        msg = "Sessão expirou. Faça login novamente.";
      }
      setError(msg);
      toast.error(msg);
    }
  };

  // ═════════════ RENDER ═════════════

  // ───── STEP 0: SIGNUP ─────
  if (step === 0) {
    return (
      <StepShell
        headerExtras={
          <button onClick={() => navigate("/login")} className="text-[13px] font-medium text-zinc-600 hover:text-zinc-900" data-testid="gs-go-login">
            Entrar
          </button>
        }
      >
        <ProgressBar value={(1 / totalSteps) * 100} />
        <div className="mt-8">
          <h1 className="text-[28px] sm:text-[34px] font-semibold tracking-tight text-zinc-900 leading-tight" style={{ fontFamily: "'Inter', sans-serif", fontWeight: 600 }}>
            Crie sua conta
          </h1>
          <p className="text-[14px] text-zinc-500 mt-1.5">7 dias grátis · cancele quando quiser</p>
        </div>

        <form
          className="mt-8 space-y-4"
          onSubmit={(e) => { e.preventDefault(); submitSignup(); }}
        >
          <PremiumInput icon={<User size={15} />} label="Nome completo" value={signup.name} onChange={(v) => setSignup((s) => ({ ...s, name: v }))} placeholder="Seu nome completo" testid="gs-name" />
          <PremiumInput icon={<Mail size={15} />} label="Email" type="email" value={signup.email} onChange={(v) => setSignup((s) => ({ ...s, email: v }))} placeholder="seu@email.com" testid="gs-email" />
          <PremiumInput icon={<Phone size={15} />} label="WhatsApp" type="tel" value={signup.phone} onChange={(v) => setSignup((s) => ({ ...s, phone: maskPhone(v) }))} placeholder="(11) 98765-4321" testid="gs-phone" />
          <div>
            <PremiumInput
              icon={<Lock size={15} />}
              label="Senha"
              type={showPwd ? "text" : "password"}
              value={signup.password}
              onChange={(v) => setSignup((s) => ({ ...s, password: v }))}
              placeholder="Mínimo 6 caracteres"
              testid="gs-password"
              rightAdornment={
                <button type="button" onClick={() => setShowPwd((v) => !v)} className="text-[11px] text-zinc-500 hover:text-zinc-900 font-medium" data-testid="gs-toggle-pwd">
                  {showPwd ? "Ocultar" : "Mostrar"}
                </button>
              }
            />
            {signup.password && (
              <div className="mt-1.5">
                <div className="h-1 flex gap-1 rounded overflow-hidden">
                  {[1, 2, 3, 4, 5].map((i) => (
                    <span key={i} className={`flex-1 rounded ${pwdStrength.score >= i ? pwdStrength.color : "bg-zinc-200"}`} />
                  ))}
                </div>
                <p className="text-[10.5px] text-zinc-500 mt-1">{pwdStrength.label}</p>
              </div>
            )}
          </div>
          <PremiumInput icon={<Lock size={15} />} label="Confirmar senha" type="password" value={signup.confirm} onChange={(v) => setSignup((s) => ({ ...s, confirm: v }))} placeholder="Digite a senha novamente" testid="gs-confirm" />

          {error && (
            <div className="rounded-lg bg-rose-50 border border-rose-100 px-3 py-2 text-[12px] text-rose-700 flex items-start gap-2" data-testid="gs-error">
              <X size={13} className="flex-shrink-0 mt-0.5" /> <span>{error}</span>
            </div>
          )}

          <label className="flex items-start gap-2.5 cursor-pointer select-none pt-0.5" data-testid="gs-accept-terms-label">
            <input
              type="checkbox"
              checked={signup.accepted}
              onChange={(e) => setSignup((s) => ({ ...s, accepted: e.target.checked }))}
              className="mt-0.5 w-[18px] h-[18px] rounded border-zinc-300 text-violet-600 focus:ring-violet-500 cursor-pointer flex-shrink-0"
              data-testid="gs-accept-terms"
            />
            <span className="text-[12px] text-zinc-500 leading-snug">
              Li e aceito os{" "}
              <a href="/termos" target="_blank" rel="noopener noreferrer" className="underline font-medium text-zinc-700 hover:text-violet-700">Termos de Uso</a>{" "}
              e a{" "}
              <a href="/privacidade" target="_blank" rel="noopener noreferrer" className="underline font-medium text-zinc-700 hover:text-violet-700">Política de Privacidade</a>.
            </span>
          </label>

          <button
            type="submit"
            disabled={loading}
            className="w-full h-12 inline-flex items-center justify-center gap-2 rounded-full text-white text-[14px] font-semibold tracking-tight active:scale-[0.98] transition-transform disabled:opacity-60"
            style={{
              backgroundImage: "linear-gradient(95deg, #6B3DF6 0%, #FF4D8D 55%, #FFB347 100%)",
              boxShadow: "0 12px 32px -10px rgba(107,61,246,0.45)",
            }}
            data-testid="gs-submit"
          >
            {loading ? <Loader2 size={15} className="animate-spin" /> : null}
            {loading ? "Criando sua conta…" : "Continuar"}
            {!loading && <ArrowRight size={15} />}
          </button>
        </form>
      </StepShell>
    );
  }

  // ───── ONBOARDING STEPS 1..5 ─────
  if (step >= 1 && step <= ONBOARDING_STEPS.length) {
    const q = ONBOARDING_STEPS[step - 1];
    return (
      <StepShell
        headerExtras={
          <button onClick={() => setStep((s) => Math.max(0, s - 1))} className="text-[12.5px] font-medium text-zinc-600 hover:text-zinc-900 inline-flex items-center gap-1" data-testid="gs-back">
            <ArrowLeft size={13} /> Voltar
          </button>
        }
      >
        <ProgressBar value={((step + 1) / totalSteps) * 100} />
        <p className="text-[11px] font-medium uppercase tracking-[0.18em] text-zinc-400 mt-6">Etapa {step} de {ONBOARDING_STEPS.length}</p>
        <h1 className="text-[26px] sm:text-[32px] font-semibold tracking-tight text-zinc-900 leading-tight mt-2" style={{ fontFamily: "'Inter', sans-serif", fontWeight: 600 }}>
          {q.title}
        </h1>
        <p className="text-[14px] text-zinc-500 mt-1.5">{q.subtitle}</p>

        <div className="mt-7 space-y-2.5" key={q.id}>
          {q.options.map((opt, idx) => {
            const Icon = opt.icon || Check;
            const selected = answers[q.id] === opt.id;
            return (
              <button
                key={opt.id}
                type="button"
                onClick={() => selectAnswer(q.id, opt.id)}
                className={`w-full px-5 py-4 rounded-2xl border text-left flex items-center gap-4 transition-all duration-200 active:scale-[0.99] ${
                  selected ? "border-violet-500 bg-violet-50/40 shadow-md" : "border-zinc-200 bg-white hover:border-zinc-300 hover:shadow-sm"
                }`}
                style={{ animation: `gsStepFade 0.32s ${idx * 0.04}s both` }}
                data-testid={`gs-opt-${q.id}-${opt.id}`}
              >
                <span
                  className={`inline-flex items-center justify-center w-10 h-10 rounded-xl flex-shrink-0 transition-colors ${
                    selected ? "text-white" : "text-zinc-600 bg-zinc-100"
                  }`}
                  style={selected ? { backgroundImage: "linear-gradient(135deg, #6B3DF6 0%, #FF4D8D 100%)" } : {}}
                >
                  <Icon size={18} strokeWidth={1.9} />
                </span>
                <div className="flex-1 min-w-0">
                  <p className="text-[14.5px] font-semibold text-zinc-900">{opt.label}</p>
                  {opt.sub && <p className="text-[11.5px] text-zinc-500 mt-0.5">{opt.sub}</p>}
                </div>
                {selected ? (
                  <Check size={18} className="text-violet-600 flex-shrink-0" strokeWidth={2.5} />
                ) : (
                  <ArrowRight size={16} className="text-zinc-300 flex-shrink-0" />
                )}
              </button>
            );
          })}
        </div>

        <style>{`
          @keyframes gsStepFade {
            from { opacity: 0; transform: translateY(8px); }
            to { opacity: 1; transform: translateY(0); }
          }
        `}</style>
      </StepShell>
    );
  }

  // ───── SUB-STEP: Tipo de profissional (após selecionar "professional" em usage_intent) ─────
  if (step === ONBOARDING_STEPS.length + 0.5) {
    return (
      <StepShell
        headerExtras={
          <button onClick={() => setStep(ONBOARDING_STEPS.length)} className="text-[12.5px] font-medium text-zinc-600 hover:text-zinc-900 inline-flex items-center gap-1" data-testid="gs-back">
            <ArrowLeft size={13} /> Voltar
          </button>
        }
      >
        <ProgressBar value={((ONBOARDING_STEPS.length + 0.5) / totalSteps) * 100} />
        <p className="text-[11px] font-medium uppercase tracking-[0.18em] text-zinc-400 mt-6">Mais um detalhe</p>
        <h1 className="text-[26px] sm:text-[32px] font-semibold tracking-tight text-zinc-900 leading-tight mt-2" style={{ fontFamily: "'Inter', sans-serif", fontWeight: 600 }}>
          Qual seu segmento profissional?
        </h1>
        <p className="text-[14px] text-zinc-500 mt-1.5">Opcional — ajuda a sugerir templates e recursos.</p>

        <div className="mt-7 space-y-2.5">
          {PROFESSIONAL_TYPE_OPTIONS.map((opt, idx) => {
            const Icon = opt.icon || Briefcase;
            const selected = answers.professional_type === opt.id;
            return (
              <button
                key={opt.id}
                type="button"
                onClick={() => selectProfessionalType(opt.id)}
                className={`w-full px-5 py-4 rounded-2xl border text-left flex items-center gap-4 transition-all duration-200 active:scale-[0.99] ${
                  selected ? "border-violet-500 bg-violet-50/40 shadow-md" : "border-zinc-200 bg-white hover:border-zinc-300 hover:shadow-sm"
                }`}
                style={{ animation: `gsStepFade 0.32s ${idx * 0.04}s both` }}
                data-testid={`gs-pro-type-${opt.id}`}
              >
                <span
                  className={`inline-flex items-center justify-center w-10 h-10 rounded-xl flex-shrink-0 transition-colors ${
                    selected ? "text-white" : "text-zinc-600 bg-zinc-100"
                  }`}
                  style={selected ? { backgroundImage: "linear-gradient(135deg, #6B3DF6 0%, #FF4D8D 100%)" } : {}}
                >
                  <Icon size={18} strokeWidth={1.9} />
                </span>
                <div className="flex-1 min-w-0">
                  <p className="text-[14.5px] font-semibold text-zinc-900">{opt.label}</p>
                </div>
                <ArrowRight size={16} className="text-zinc-300 flex-shrink-0" />
              </button>
            );
          })}
          <button
            type="button"
            onClick={() => selectProfessionalType("skipped")}
            className="w-full mt-2 py-3 text-[12.5px] text-zinc-500 hover:text-zinc-900 transition-colors"
            data-testid="gs-pro-skip"
          >
            Pular essa etapa
          </button>
        </div>
        <style>{`
          @keyframes gsStepFade { from { opacity: 0; transform: translateY(8px); } to { opacity: 1; transform: translateY(0); } }
        `}</style>
      </StepShell>
    );
  }

  // ───── TRIAL WELCOME STEP (sem cobrança no signup) ─────
  if (step === ONBOARDING_STEPS.length + 1) {
    const goToDashboard = () => {
      navigate("/dashboard");
    };
    return (
      <StepShell
        headerExtras={
          <button onClick={() => setStep((s) => Math.max(0, s - 1))} className="text-[12.5px] font-medium text-zinc-600 hover:text-zinc-900 inline-flex items-center gap-1">
            <ArrowLeft size={13} /> Voltar
          </button>
        }
      >
        <ProgressBar value={100} />
        <div className="mt-7 text-center">
          <span className="inline-flex items-center gap-1.5 px-3 py-1 rounded-full text-white text-[11.5px] font-semibold"
            style={{ backgroundImage: "linear-gradient(135deg, #6B3DF6 0%, #FF4D8D 100%)" }}>
            <Sparkles size={12} /> 7 dias premium liberados
          </span>
          <h1 className="text-[28px] sm:text-[34px] font-semibold tracking-tight text-zinc-900 leading-tight mt-3" style={{ fontFamily: "'Inter', sans-serif", fontWeight: 600 }}>
            Sua conta está pronta!
          </h1>
          <p className="text-[14px] text-zinc-500 mt-2 max-w-md mx-auto">
            Você tem <strong className="text-zinc-900">7 dias gratuitos com acesso completo</strong> —
            sem cartão, sem cobrança, sem limites. Após esse período, escolha um plano para continuar.
          </p>
        </div>

        <div className="mt-7 rounded-3xl bg-white border-2 border-violet-200 p-6 shadow-xl">
          <div className="grid grid-cols-1 gap-2.5">
            {[
              "Eventos ilimitados",
              "Convidados ilimitados",
              "Envio em massa pelo WhatsApp",
              "Confirmação RSVP + QR Code",
              "Check-in em tempo real",
              "Assistente IA + Orçamento",
            ].map((it, i) => (
              <div key={i} className="flex items-start gap-2.5 text-[13.5px] text-zinc-800">
                <Check size={15} className="text-emerald-600 flex-shrink-0 mt-0.5" strokeWidth={3} />
                <span>{it}</span>
              </div>
            ))}
          </div>

          <button
            onClick={goToDashboard}
            className="w-full mt-6 h-12 inline-flex items-center justify-center gap-2 rounded-full text-white text-[14px] font-semibold tracking-tight active:scale-[0.98] transition-transform"
            style={{
              backgroundImage: "linear-gradient(95deg, #6B3DF6 0%, #FF4D8D 55%, #FFB347 100%)",
              boxShadow: "0 12px 32px -10px rgba(107,61,246,0.45)",
            }}
            data-testid="gs-go-dashboard"
          >
            Começar a usar agora
            <ArrowRight size={15} />
          </button>
          <p className="text-[11.5px] text-zinc-400 text-center mt-3">
            Nenhuma cobrança será feita durante os 7 dias.
          </p>
        </div>

        {/* Trust */}
        <div className="grid grid-cols-3 gap-2 mt-5 text-center">
          <div className="flex flex-col items-center gap-1.5 px-2 py-3 rounded-xl bg-white border border-zinc-100">
            <ShieldCheck size={16} className="text-emerald-600" />
            <p className="text-[10.5px] font-medium text-zinc-700">Sem cartão exigido</p>
          </div>
          <div className="flex flex-col items-center gap-1.5 px-2 py-3 rounded-xl bg-white border border-zinc-100">
            <Sparkles size={16} className="text-pink-500" />
            <p className="text-[10.5px] font-medium text-zinc-700">Acesso completo por 7 dias</p>
          </div>
          <div className="flex flex-col items-center gap-1.5 px-2 py-3 rounded-xl bg-white border border-zinc-100">
            <RefreshCw size={16} className="text-violet-600" />
            <p className="text-[10.5px] font-medium text-zinc-700">Assine quando quiser</p>
          </div>
        </div>
      </StepShell>
    );
  }

  return null;
}

// ═════════════ HELPERS ═════════════
function maskPhone(v) {
  const d = (v || "").replace(/\D/g, "").slice(0, 11);
  if (d.length <= 2) return d;
  if (d.length <= 6) return `(${d.slice(0, 2)}) ${d.slice(2)}`;
  if (d.length <= 10) return `(${d.slice(0, 2)}) ${d.slice(2, 6)}-${d.slice(6)}`;
  return `(${d.slice(0, 2)}) ${d.slice(2, 7)}-${d.slice(7)}`;
}

function PremiumInput({ icon, label, value, onChange, type = "text", placeholder, testid, rightAdornment }) {
  return (
    <div>
      <div className="flex items-center justify-between mb-1.5">
        <label className="block text-[11px] font-medium text-zinc-500 tracking-wide uppercase">{label}</label>
        {rightAdornment}
      </div>
      <div className="relative">
        <span className="absolute left-3.5 top-1/2 -translate-y-1/2 text-zinc-400">{icon}</span>
        <input
          type={type}
          value={value}
          onChange={(e) => onChange(e.target.value)}
          placeholder={placeholder}
          data-testid={testid}
          className="w-full h-12 pl-10 pr-3 text-[14.5px] rounded-xl border border-zinc-200 bg-white text-zinc-900 placeholder:text-zinc-300 focus:outline-none focus:border-violet-500 focus:ring-4 focus:ring-violet-100 transition-all"
        />
      </div>
    </div>
  );
}
